/**
 * Content Editor Types
 * 
 * Shared type definitions for the unified block-based content editor.
 */

// ============================================================================
// Digital Page Block Types
// ============================================================================

/**
 * A formatted line within a digital page block.
 * Supports basic formatting: indent levels, bold, italic.
 */
export interface FormattedLine {
  text: string;
  indent: number;      // 0, 1, 2 indent levels
  bold?: boolean;
  italic?: boolean;
}

/**
 * A digital page block - represents one reading screen.
 * This is the core unit of the block editor.
 */
export interface DigitalPageBlock {
  id: string;
  pageNumber: number;
  lines: FormattedLine[];
  sourcePageIds: string[];  // Which scanned pages contributed to this block
}

/**
 * Convert plain text to FormattedLine array.
 * Parses leading spaces/tabs into indent levels (2 spaces or 1 tab = 1 indent).
 */
export function textToLines(text: string): FormattedLine[] {
  return text.split('\n').map(line => {
    // Parse leading whitespace into indent levels
    const match = line.match(/^(\s*)/);
    const leadingWhitespace = match ? match[1] : '';
    
    // Count indent: 2 spaces or 1 tab = 1 indent level
    let indent = 0;
    for (let i = 0; i < leadingWhitespace.length; i++) {
      if (leadingWhitespace[i] === '\t') {
        indent++;
      } else if (leadingWhitespace[i] === ' ' && leadingWhitespace[i + 1] === ' ') {
        indent++;
        i++; // Skip the second space
      }
    }
    
    return {
      text: line.trimStart(), // Remove leading whitespace from text
      indent: Math.min(indent, 3), // Max 3 indent levels
    };
  });
}

/**
 * Convert FormattedLine array back to plain text with markdown formatting.
 * Use this for storage/serialization where formatting needs to be preserved.
 */
export function linesToText(lines: FormattedLine[]): string {
  return lines.map(line => {
    const indent = '  '.repeat(line.indent);
    let text = line.text;
    if (line.bold) text = `**${text}**`;
    if (line.italic) text = `_${text}_`;
    return indent + text;
  }).join('\n');
}

/**
 * Convert FormattedLine array to plain text WITHOUT markdown formatting.
 * Use this for reading views where text is rendered with CSS styling.
 */
export function linesToPlainText(lines: FormattedLine[]): string {
  return lines.map(line => line.text).join('\n');
}

/**
 * Convert FormattedLine array to HTML for rendering.
 */
export function linesToHtml(lines: FormattedLine[]): string {
  return lines.map(line => {
    let text = escapeHtml(line.text);
    if (line.bold) text = `<strong>${text}</strong>`;
    if (line.italic) text = `<em>${text}</em>`;
    const marginLeft = line.indent * 24; // 24px per indent level
    return `<div style="margin-left: ${marginLeft}px">${text || '&nbsp;'}</div>`;
  }).join('');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// ============================================================================
// Region Selection Types
// ============================================================================

/**
 * A selected region on the scanned image for re-OCR.
 */
export interface RegionSelection {
  x: number;           // Left edge (0-1 normalized)
  y: number;           // Top edge (0-1 normalized)
  width: number;       // Width (0-1 normalized)
  height: number;      // Height (0-1 normalized)
  pageIndex: number;   // Which scanned page this region is on
}

/**
 * State for pending region OCR extraction.
 */
export interface PendingExtraction {
  region: RegionSelection;
  extractedText: string | null;
  inferredInsertIndex: number;
  status: 'extracting' | 'ready' | 'error';
  error?: string;
}

// ============================================================================
// Editor State Types
// ============================================================================

/**
 * Complete editor state.
 */
export interface EditorState {
  blocks: DigitalPageBlock[];
  selectedBlockId: string | null;
  cursorPosition: number;          // Character position within selected block
  pendingExtraction: PendingExtraction | null;
  isDirty: boolean;
  lastSaved: Date | null;
}

/**
 * Scanned page data for the image panel.
 */
export interface ScannedPageData {
  id: string;
  pageNumber: number;
  imageUrl: string | null;
  enhancedImageUrl: string | null;
  extractedText: string;
  pageType: string;
  isTwoPageSpread?: boolean;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a unique block ID.
 */
export function generateBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Infer insertion index based on region's vertical position.
 * Maps the region's Y-center to a line index in the existing text.
 */
export function inferInsertionIndex(
  region: RegionSelection,
  existingLineCount: number
): number {
  // Region's vertical center (already normalized 0-1)
  const verticalPosition = region.y + region.height / 2;
  
  // Map to line index
  const insertIndex = Math.round(verticalPosition * existingLineCount);
  
  return Math.max(0, Math.min(insertIndex, existingLineCount));
}

/**
 * Create a new empty block.
 */
export function createEmptyBlock(pageNumber: number, sourcePageIds: string[] = []): DigitalPageBlock {
  return {
    id: generateBlockId(),
    pageNumber,
    lines: [{ text: '', indent: 0 }],
    sourcePageIds,
  };
}

/**
 * Split a block at a specific line index (line boundary only).
 */
export function splitBlockAtLine(
  block: DigitalPageBlock,
  lineIndex: number
): [DigitalPageBlock, DigitalPageBlock] {
  const firstLines = block.lines.slice(0, lineIndex);
  const secondLines = block.lines.slice(lineIndex);

  return [
    {
      ...block,
      lines: firstLines.length > 0 ? firstLines : [{ text: '', indent: 0 }],
    },
    {
      id: generateBlockId(),
      pageNumber: block.pageNumber + 1,
      lines: secondLines.length > 0 ? secondLines : [{ text: '', indent: 0 }],
      sourcePageIds: block.sourcePageIds,
    },
  ];
}

/**
 * Split a block at the cursor: text before cursor (including on current line) stays in the first
 * block; text at and after the cursor goes to the second block. Supports splitting within a line.
 */
export function splitBlockAtCursor(
  block: DigitalPageBlock,
  lineIndex: number,
  offsetInLine: number
): [DigitalPageBlock, DigitalPageBlock] {
  const line = block.lines[lineIndex] ?? { text: '', indent: 0 };
  const beforeLine = block.lines.slice(0, lineIndex);
  const afterLine = block.lines.slice(lineIndex + 1);
  const indent = line.indent;
  const text = line.text;
  const left = text.slice(0, offsetInLine);
  const right = text.slice(offsetInLine);

  const firstLines: FormattedLine[] =
    beforeLine.length > 0 || left.length > 0
      ? [...beforeLine, { ...line, text: left }]
      : [{ text: '', indent: 0 }];
  const secondLines: FormattedLine[] =
    right.length > 0 || afterLine.length > 0
      ? [{ text: right, indent }, ...afterLine]
      : [{ text: '', indent: 0 }];

  return [
    {
      ...block,
      lines: firstLines,
    },
    {
      id: generateBlockId(),
      pageNumber: block.pageNumber + 1,
      lines: secondLines,
      sourcePageIds: block.sourcePageIds,
    },
  ];
}

/**
 * Merge two blocks into one.
 */
export function mergeBlocks(
  first: DigitalPageBlock,
  second: DigitalPageBlock
): DigitalPageBlock {
  return {
    ...first,
    lines: [...first.lines, ...second.lines],
    sourcePageIds: [...new Set([...first.sourcePageIds, ...second.sourcePageIds])],
  };
}
